import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import IconButton from "@material-ui/core/IconButton";
// import DeleteIcon from "@material-ui/icons/Delete";
import ActionDialog from "@saleor/components/ActionDialog";
import NotFoundPage from "@saleor/components/NotFoundPage";
import RecommendProductDialog from "@saleor/components/RecommendProductDialog";
import { RecommendProductTab } from "@saleor/components/RecommendProductSection";
import { WindowTitle } from "@saleor/components/WindowTitle";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { commonMessages } from "@saleor/intl";
import { useUnassignRecommendProductMutation } from "@saleor/products/mutations";
import {
  RecommendationTargetTypeEnum,
  RecommendationTypeEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@saleor/utils/handlers/metadataUpdateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import copy from "copy-to-clipboard";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { PAGINATE_BY } from "../../config";
import { maybe } from "../../misc";
import { useProductBulkDeleteMutation } from "../../products/mutations";
import { productAddUrl, productUrl } from "../../products/urls";
import { CategoryInput } from "../../types/globalTypes";
import {
  CategoryPageTab,
  CategoryUpdatePage,
  FormData
} from "../components/CategoryUpdatePage/CategoryUpdatePage";
import {
  useCategoryBulkDeleteMutation,
  useCategoryDeleteMutation,
  useCategoryUpdateMutation
} from "../mutations";
import { useCategoryDetailsQuery } from "../queries";
import { CategoryBulkDelete } from "../types/CategoryBulkDelete";
import { CategoryDelete } from "../types/CategoryDelete";
import { CategoryUpdate } from "../types/CategoryUpdate";
import {
  categoryAddUrl,
  categoryListUrl,
  categoryUrl,
  CategoryUrlDialog,
  CategoryUrlQueryParams
} from "../urls";

export interface CategoryDetailsProps {
  params: CategoryUrlQueryParams;
  id: string;
}

export function getActiveTab(tabName: string): CategoryPageTab {
  return tabName === CategoryPageTab.products
    ? CategoryPageTab.products
    : CategoryPageTab.categories;
}

export function getActiveRecommendTab(tabName: string): RecommendProductTab {
  return tabName === RecommendProductTab.also_likes
    ? RecommendProductTab.also_likes
    : RecommendProductTab.recommends;
}

export const CategoryDetails: React.FC<CategoryDetailsProps> = ({
  id,
  params
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const { data, loading, refetch } = useCategoryDetailsQuery({
    displayLoader: true,
    variables: { ...paginationState, id }
  });

  const category = data?.category;

  if (category === null) {
    return <NotFoundPage onBack={() => navigate(categoryListUrl())} />;
  }

  const handleCategoryDelete = (data: CategoryDelete) => {
    if (data.categoryDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Category deleted"
        })
      });
      navigate(categoryListUrl());
    }
  };

  const [deleteCategory, deleteResult] = useCategoryDeleteMutation({
    onCompleted: handleCategoryDelete
  });

  const handleCategoryUpdate = (data: CategoryUpdate) => {
    if (data.categoryUpdate.errors.length > 0) {
      const backgroundImageError = data.categoryUpdate.errors.find(
        error => error.field === ("backgroundImage" as keyof CategoryInput)
      );
      if (backgroundImageError) {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      }
    }
  };

  const [updateCategory, updateResult] = useCategoryUpdateMutation({
    onCompleted: handleCategoryUpdate
  });

  const handleBulkCategoryDelete = (data: CategoryBulkDelete) => {
    if (data.categoryBulkDelete.errors.length === 0) {
      closeModal();
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      reset();
    }
  };

  const [
    categoryBulkDelete,
    categoryBulkDeleteOpts
  ] = useCategoryBulkDeleteMutation({
    onCompleted: handleBulkCategoryDelete
  });

  const [
    productBulkDelete,
    productBulkDeleteOpts
  ] = useProductBulkDeleteMutation({
    onCompleted: data => {
      if (data.productBulkDelete.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        refetch();
        reset();
      }
    }
  });

  const [unassignProduct] = useUnassignRecommendProductMutation({
    onCompleted: data => {
      if (data.recommendProductRemove.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted recommend product from product."
          })
        });

        // closeModal();
        reset();
      }
    }
  });

  const changeTab = (tabName: CategoryPageTab) => {
    reset();
    navigate(
      categoryUrl(id, {
        activeTab: tabName
      })
    );
  };

  // const changeRecommendTab = (tabName: RecommendProductTab) => {
  //   reset();
  //   navigate(
  //     categoryUrl(id, {
  //       activeRecommendTab: tabName
  //     })
  //   );
  // };

  const [openModal, closeModal] = createDialogActionHandlers<
    CategoryUrlDialog,
    CategoryUrlQueryParams
  >(navigate, params => categoryUrl(id, params), params);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    params.activeTab === CategoryPageTab.categories
      ? maybe(() => data.category.children.pageInfo)
      : maybe(() => data.category.products.pageInfo),
    paginationState,
    params
  );

  const handleUpdate = async (formData: FormData) => {
    const result = await updateCategory({
      variables: {
        id,
        input: {
          backgroundImageAlt: formData.backgroundImageAlt,
          descriptionJson: JSON.stringify(formData.description),
          name: formData.name,
          seo: {
            description: formData.seoDescription,
            title: formData.seoTitle,
            keyword: formData.seoKeyword
          },
          slug: formData.slug
        }
      }
    });

    return result.data.categoryUpdate.errors;
  };
  const handleSubmit = createMetadataUpdateHandler(
    data?.category,
    handleUpdate,
    variables => updateMetadata({ variables }),
    variables => updatePrivateMetadata({ variables })
  );

  return (
    <>
      <WindowTitle title={maybe(() => data.category.name)} />
      <CategoryUpdatePage
        changeTab={changeTab}
        currentTab={params.activeTab}
        // changeRecommendTab={changeRecommendTab}
        // currentRecommendTab={params.activeRecommendTab}
        category={maybe(() => data.category)}
        disabled={loading}
        errors={updateResult.data?.categoryUpdate.errors || []}
        onAddCategory={() => navigate(categoryAddUrl(id))}
        onAddProduct={() => navigate(productAddUrl())}
        onCopyIds={() => {
          openModal("copyids");
        }}
        onBack={() =>
          navigate(
            maybe(() => categoryUrl(data.category.parent.id), categoryListUrl())
          )
        }
        onCategoryClick={id => () => navigate(categoryUrl(id))}
        onDelete={() => openModal("delete")}
        onImageDelete={() =>
          updateCategory({
            variables: {
              id,
              input: {
                backgroundImage: null
              }
            }
          })
        }
        onImageUpload={file =>
          updateCategory({
            variables: {
              id,
              input: {
                backgroundImage: file
              }
            }
          })
        }
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        pageInfo={pageInfo}
        onProductClick={id => () => navigate(productUrl(id))}
        onSubmit={handleSubmit}
        onRecommendAssign={() => openModal("assign-recommend")}
        onAlsolikeAssign={() => openModal("assign-alsolike")}
        onRecommendUnassign={(productId, event) => {
          event.stopPropagation();
          unassignProduct({
            variables: {
              targetId: id,
              targetType: RecommendationTargetTypeEnum.CATEGORY,
              recommendType: RecommendationTypeEnum.RECOMMEND,
              productIds: [productId]
            }
          });
        }}
        onAlsolikeUnassign={(productId, event) => {
          event.stopPropagation();
          unassignProduct({
            variables: {
              targetId: id,
              targetType: RecommendationTargetTypeEnum.CATEGORY,
              recommendType: RecommendationTypeEnum.ALSO_LIKE,
              productIds: [productId]
            }
          });
        }}
        products={maybe(() =>
          data.category.products.edges.map(edge => edge.node)
        )}
        saveButtonBarState={updateResult.status}
        subcategories={maybe(() =>
          data.category.children.edges.map(edge => edge.node)
        )}
        subcategoryListToolbar={
          <IconButton
            color="primary"
            // onClick={() =>
            //   openModal("delete-categories", {
            //     ids: listElements
            //   })
            // }
          >
            {/* <DeleteIcon /> */}
          </IconButton>
        }
        productListToolbar={
          <IconButton
            color="primary"
            // onClick={() =>
            //   openModal("delete-products", {
            //     ids: listElements
            //   })
            // }
          >
            {/* <DeleteIcon /> */}
          </IconButton>
        }
        recommendToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-recommend", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign recommend product from category, button"
            />
          </Button>
        }
        alsolikeToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-alsolike", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign alsolike product from category, button"
            />
          </Button>
        }
        buytogetherToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-buytogether", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign alsolike product from category, button"
            />
          </Button>
        }
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
      />
      <ActionDialog
        confirmButtonState={deleteResult.status}
        onClose={closeModal}
        onConfirm={() => deleteCategory({ variables: { id } })}
        open={params.action === "delete"}
        title={intl.formatMessage({
          defaultMessage: "Delete category",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to delete {categoryName}?"
            values={{
              categoryName: (
                <strong>{maybe(() => data.category.name, "...")}</strong>
              )
            }}
          />
        </DialogContentText>
        <DialogContentText>
          <FormattedMessage defaultMessage="Remember this will also unpin all products assigned to this category, making them unavailable in storefront." />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={
          params.action === "delete-categories" &&
          maybe(() => params.ids.length > 0)
        }
        confirmButtonState={categoryBulkDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          categoryBulkDelete({
            variables: { ids: params.ids }
          }).then(() => refetch())
        }
        title={intl.formatMessage({
          defaultMessage: "Delete categories",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this category?} other{Are you sure you want to delete {displayQuantity} categories?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
        <DialogContentText>
          <FormattedMessage defaultMessage="Remember this will also delete all products assigned to this category." />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "delete-products"}
        confirmButtonState={productBulkDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          productBulkDelete({
            variables: { ids: params.ids }
          }).then(() => refetch())
        }
        title={intl.formatMessage({
          defaultMessage: "Delete products",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this product?} other{Are you sure you want to delete {displayQuantity} products?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <RecommendProductDialog
        targetId={id}
        targetType={RecommendationTargetTypeEnum.CATEGORY}
        closeModal={closeModal}
        reset={reset}
        action={params.action}
        ids={params.ids}
      />

      <ActionDialog
        confirmButtonState={updateResult.status}
        onClose={closeModal}
        onConfirm={() => {
          copy(data?.category?.categoryVariantIds);
          notify({
            status: "success",
            text: "Copy ids successfully"
          });
          closeModal();
        }}
        open={params.action === "copyids"}
        title={intl.formatMessage({
          defaultMessage: "Copy default variant ids",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{data}"
            values={{
              data: (
                <strong>
                  {maybe(() => data?.category?.categoryVariantIds, "...")}
                </strong>
              )
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};
export default CategoryDetails;
